import React from "react";

const COLUMNS = [
  { key: "thumb", label: "", sortable: false },
  { key: "product_type", label: "Artist", sortable: true },
  { key: "title", label: "Title", sortable: true },
  { key: "variant_title", label: "Variant", sortable: false },
  { key: "object_id", label: "Variant ID", sortable: false },
  { key: "price_eur", label: "Price (EUR)", sortable: true },
  { key: "discogs_lowest_price", label: "Discogs €", sortable: true },
  { key: "discogs_hotness_score", label: "Hotness", sortable: true },
  { key: "inventory_quantity", label: "Stock", sortable: true },
  { key: "badges", label: "Badges", sortable: false },
  { key: "release_date", label: "Release", sortable: true },
  { key: "created_at_shopify", label: "Added", sortable: true },
];

function HotnessBar({ score }) {
  if (score == null) return <span style={{ color: "#555" }}>—</span>;
  // Score is 0-100, map to color and width
  const pct = Math.min(100, Math.max(0, score));
  const hue = Math.round((pct / 100) * 120); // 0 = red, 120 = green
  const color = `hsl(${hue}, 70%, 50%)`;
  return (
    <div
      className="hotness-bar"
      title={`Hotness: ${score.toFixed(1)}`}
      style={{ display: "flex", alignItems: "center", gap: 4 }}
    >
      <div
        style={{
          width: 40,
          height: 6,
          background: "#333",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: color,
          }}
        />
      </div>
      <span style={{ fontSize: 11, color: "#aaa" }}>{score.toFixed(0)}</span>
    </div>
  );
}

function SortIndicator({ ordering, field }) {
  if (ordering === field) return <span className="sort-indicator">▲</span>;
  if (ordering === `-${field}`)
    return <span className="sort-indicator">▼</span>;
  return null;
}

export default function ProductList({
  products,
  loading,
  lastProductRef,
  onSort,
  ordering,
  onRowClick,
}) {
  return (
    <div className="table-container">
      <table>
        <colgroup>
          <col className="col-thumb" />
          <col className="col-artist" />
          <col className="col-title" />
          <col className="col-variant" />
          <col className="col-variant-id" />
          <col className="col-price" />
          <col className="col-discogs-price" />
          <col className="col-hotness" />
          <col className="col-stock" />
          <col className="col-badges" />
          <col className="col-release" />
          <col className="col-added" />
        </colgroup>
        <thead>
          <tr>
            {COLUMNS.map((col) => (
              <th
                key={col.key}
                className={col.sortable ? "sortable" : ""}
                onClick={col.sortable ? () => onSort(col.key) : undefined}
              >
                {col.label}
                {col.sortable && (
                  <SortIndicator ordering={ordering} field={col.key} />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {products.map((product, idx) => {
            const isLast = idx === products.length - 1;
            return (
              <tr
                key={product.id}
                ref={isLast ? lastProductRef : null}
                className={`clickable-row${!product.inventory_available ? " out-of-stock" : ""}`}
                onClick={() => onRowClick(product)}
              >
                <td>
                  {product.image || product.product_image ? (
                    <img
                      src={product.image || product.product_image}
                      alt=""
                      className="thumb"
                      loading="lazy"
                    />
                  ) : (
                    <div
                      className="thumb"
                      style={{ background: product.colour_swatch || "#333" }}
                    />
                  )}
                </td>
                <td title={product.product_type}>{product.product_type}</td>
                <td title={product.title}>{product.title}</td>
                <td title={product.variant_title}>
                  {product.colour_swatch && (
                    <span
                      className="colour-dot"
                      style={{ backgroundColor: product.colour_swatch }}
                    />
                  )}
                  {product.variant_title}
                </td>
                <td>{product.object_id || "—"}</td>
                <td>
                  {product.price_eur != null
                    ? `€${Number(product.price_eur).toFixed(2)}`
                    : "—"}
                  {product.is_on_sale && (
                    <span
                      className="badge badge-sale"
                      style={{ marginLeft: 4 }}
                    >
                      SALE
                    </span>
                  )}
                </td>
                <td>
                  {product.discogs_lowest_price != null
                    ? `€${Number(product.discogs_lowest_price).toFixed(2)}`
                    : "—"}
                </td>
                <td>
                  <HotnessBar score={product.discogs_hotness_score} />
                </td>
                <td>
                  {product.inventory_available ? (
                    <span style={{ color: "#4ade80" }}>
                      {product.inventory_quantity > 0
                        ? product.inventory_quantity
                        : "✓"}
                    </span>
                  ) : (
                    <span style={{ color: "#888" }}>—</span>
                  )}
                </td>
                <td>
                  {product.signed && (
                    <span className="badge badge-signed">✍️ SIGNED</span>
                  )}
                  {product.exclusive && (
                    <span className="badge badge-exclusive">EXCL</span>
                  )}
                  {product.is_pre_order && (
                    <span className="badge badge-preorder">PRE-ORDER</span>
                  )}
                </td>
                <td>{product.release_date || "—"}</td>
                <td>
                  {product.created_at_shopify
                    ? new Date(product.created_at_shopify).toLocaleDateString()
                    : "—"}
                </td>
              </tr>
            );
          })}
          {loading && (
            <tr className="loading-row">
              <td colSpan={COLUMNS.length}>Loading...</td>
            </tr>
          )}
          {!loading && products.length === 0 && (
            <tr className="loading-row">
              <td colSpan={COLUMNS.length}>No products found</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
